import BatchProgressTracker from '@repo/ui/batch-progress-tracker'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useState } from 'react'

import { clearLatestBatch } from '../../api/queues/clear-latest-batch'
import { getBatchesProgress } from '../../api/queues/get-batches-progress'
import useAuth from '../hooks/use-auth'
import { QueueBatch } from '../types/models/queue/queue-batch.model'

type TrackProgressBatchesContextType = {
  batches: QueueBatch[]
  showTracker: () => void
}

export const TrackProgressBatchesContext = createContext<TrackProgressBatchesContextType>({
  batches: [],
  showTracker: () => {}
})

const TrackProgressBatchesProvider = ({ children }: PropsWithChildren) => {
  const [batches, setBatches] = useState<QueueBatch[]>([])
  const [hiddenBatchIds, setHiddenBatchIds] = useState<Set<number>>(new Set())
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { mutate: clearBatchMutation } = useMutation({
    mutationKey: ['clear-latest-batch'],
    mutationFn: clearLatestBatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['track-progress-batches'] })
    }
  })

  useQuery({
    queryKey: ['track-progress-batches'],
    queryFn: async () => {
      const data = await getBatchesProgress(user.id)
      setBatches(data)
      return data
    },
    refetchInterval: 5000,
    enabled: !!user
  })

  const handleDismiss = (batch: QueueBatch) => {
    clearBatchMutation({ id: user.id, batchId: batch.batchId })
    setHiddenBatchIds((prev) => new Set(prev).add(batch.batchId))
  }

  const visibleBatches = batches.filter((batch) => !hiddenBatchIds.has(batch.batchId))

  return (
    <TrackProgressBatchesContext.Provider
      value={{ batches, showTracker: () => setHiddenBatchIds(new Set()) }}
    >
      {visibleBatches.length > 0 && (
        <div className="w-2/4 fixed bottom-0 left-0 right-0 z-50 flex flex-col gap-2 transition-all duration-300 ease-out mx-auto mb-5">
          {visibleBatches.map((batch) => (
            <BatchProgressTracker
              key={batch.batchId}
              title={batch.title}
              batch={batch}
              onDismiss={() => handleDismiss(batch)}
            />
          ))}
        </div>
      )}
      {children}
    </TrackProgressBatchesContext.Provider>
  )
}

export default TrackProgressBatchesProvider
